import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
} from 'recharts';
import type { DashboardFiltros } from './types';
import type { IDashboardContaConciliacao, IDashboardResumoContasConciliacao } from '../../interfaces/IDashboard';
import DashboardService from '../../services/DashboardService';

type Props = {
    filtros: DashboardFiltros;
};

// Escapa valores de texto livre (conciliador, área responsável, descrição de conta etc.) antes de
// interpolá-los no HTML da janela de impressão — sem isso, um valor malicioso vira XSS refletido
// na janela aberta via window.open/document.write.
const escapeHtml = (valor: unknown): string =>
    String(valor ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

type StatusConciliacao = 'Conciliado' | 'Conciliado c/ Pendência' | 'Pendente';

type RegistroConciliacao = IDashboardContaConciliacao & {
    diferenca: number;
};

type ResumoGrupo = {
    grupo: string;
    pendentesPercentual: number;
    conciliadoPendenciaPercentual: number;
    conciliadoPercentual: number;
};

const OPCOES_PER_PAGE = [15, 50, 100];

const DashboardConciliacaoContabilFinanceira = ({ filtros }: Props) => {
    const cores = {
        fundoCard: '#ffffff',
        borda: '#d9dfdb',
        bordaSuave: '#e8ece9',
        sombra: '0 2px 10px rgba(0,0,0,0.05)',
        titulo: '#2f5f4e',
        texto: '#3e4743',
        textoSuave: '#66716c',
        verdeEscuro: '#255747',
        verdePrincipal: '#2f6b58',
        verdeClaro: '#8bc34a',
        amarelo: '#f4c542',
        laranja: '#ef7c2f',
        vermelho: '#e24a3b',
        azulTexto: '#2f5e8c',
        azulValor: '#2e5b85',
        cinzaBotao: '#f3f4f3',
    };

    const [usuarioAtivo, setUsuarioAtivo] = useState<string>('Geral');
    const [conciliadores, setConciliadores] = useState<string[]>([]);
    const [registrosPagina, setRegistrosPagina] = useState<RegistroConciliacao[]>([]);
    const [totalRegistros, setTotalRegistros] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(15);
    const [resumo, setResumo] = useState<IDashboardResumoContasConciliacao | null>(null);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [erro, setErro] = useState<string | null>(null);

    const selecionarConciliador = (nome: string) => {
        setUsuarioAtivo(nome);
        setPage(1);
    };

    const selecionarPerPage = (valor: number) => {
        setPerPage(valor);
        setPage(1);
    };

    // Reseta aba e página, e recarrega as abas de conciliador quando o filtro (empresa/período) muda.
    useEffect(() => {
        setUsuarioAtivo('Geral');
        setPage(1);

        if (!filtros.empresaSelecionada) {
            setConciliadores([]);
            return;
        }

        let ativo = true;

        DashboardService.obterConciliadoresDisponiveis(filtros.empresaSelecionada, filtros).then((lista) => {
            if (ativo) setConciliadores(lista);
        });

        return () => {
            ativo = false;
        };
    }, [
        filtros.empresaSelecionada,
        filtros.tipoPeriodo,
        filtros.anoSelecionado,
        filtros.mesSelecionado,
        filtros.diaSelecionado,
        filtros.dataInicial,
        filtros.dataFinal,
    ]);

    // Busca a página atual da tabela + o resumo agregado (painel esquerdo e rodapé).
    useEffect(() => {
        if (!filtros.empresaSelecionada) return;

        let ativo = true;

        async function carregar() {
            setCarregando(true);
            setErro(null);

            try {
                const conciliadorFiltro = usuarioAtivo === 'Geral' ? undefined : usuarioAtivo;

                const [paginado, resumoDados] = await Promise.all([
                    DashboardService.obterContasConciliacao(filtros.empresaSelecionada, filtros, conciliadorFiltro, page, perPage),
                    DashboardService.obterResumoStatusContas(filtros.empresaSelecionada, filtros, conciliadorFiltro),
                ]);

                if (!ativo) return;

                setRegistrosPagina(
                    paginado.items.map((item) => ({
                        ...item,
                        diferenca: item.saldoBalancete - item.saldoComSuporte,
                    }))
                );
                setTotalRegistros(paginado.total);
                setResumo(resumoDados);
            } catch (e: any) {
                if (ativo) setErro(e?.message ?? 'Erro ao carregar o dashboard');
            } finally {
                if (ativo) setCarregando(false);
            }
        }

        carregar();

        return () => {
            ativo = false;
        };
    }, [
        filtros.empresaSelecionada,
        filtros.tipoPeriodo,
        filtros.anoSelecionado,
        filtros.mesSelecionado,
        filtros.diaSelecionado,
        filtros.dataInicial,
        filtros.dataFinal,
        usuarioAtivo,
        page,
        perPage,
    ]);

    const formatarMoeda = (valor: number) =>
        valor.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        });

    const formatarData = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

    const totalPaginas = Math.max(1, Math.ceil(totalRegistros / perPage));

    const usuarios = useMemo(() => ['Geral', ...conciliadores], [conciliadores]);

    const pieData = useMemo(() => {
        if (!resumo) return [];

        const totalPendentes = resumo.porCategoria.reduce((acc, item) => acc + item.pendentes, 0);
        const totalConciliadoPendencia = resumo.porCategoria.reduce((acc, item) => acc + item.conciliadoComPendencia, 0);
        const totalConciliado = resumo.porCategoria.reduce((acc, item) => acc + item.conciliado, 0);
        const total = totalPendentes + totalConciliadoPendencia + totalConciliado || 1;

        return [
            { name: 'Pendentes', value: Math.round((totalPendentes / total) * 100), color: cores.vermelho },
            { name: 'Conciliado c/ Pendência', value: Math.round((totalConciliadoPendencia / total) * 100), color: cores.amarelo },
            { name: 'Conciliado', value: Math.round((totalConciliado / total) * 100), color: cores.verdeClaro },
        ].filter((item) => item.value > 0);
    }, [resumo]);

    const totaisGrupos = useMemo<ResumoGrupo[]>(() => {
        if (!resumo) return [];

        return resumo.porCategoria.map((item) => {
            const total = item.pendentes + item.conciliadoComPendencia + item.conciliado || 1;

            return {
                grupo: item.categoria,
                pendentesPercentual: (item.pendentes / total) * 100,
                conciliadoPendenciaPercentual: (item.conciliadoComPendencia / total) * 100,
                conciliadoPercentual: (item.conciliado / total) * 100,
            };
        });
    }, [resumo]);

    const totaisRodape = useMemo(
        () => ({
            contasPendentes: resumo?.contasPendentes ?? 0,
            saldoBalancete: resumo?.saldoBalancete ?? 0,
            saldoComSuporte: resumo?.saldoComSuporte ?? 0,
            diferencaTotal: (resumo?.saldoBalancete ?? 0) - (resumo?.saldoComSuporte ?? 0),
        }),
        [resumo]
    );

    const nomeArquivoBase = useMemo(() => {
        const nomeAba = usuarioAtivo === 'Geral' ? 'geral' : usuarioAtivo.toLowerCase().replace(/\s+/g, '-');
        return `relatorio-conciliacao-${nomeAba}-pagina-${page}`;
    }, [usuarioAtivo, page]);

    const exportarExcel = () => {
        const cabecalhos = [
            'Conciliador',
            'Revisão',
            'Área Responsável',
            'Período',
            'Conta Contábil',
            'Descrição Conta',
            'Saldo Balancete',
            'Saldo com Suporte',
            'Total das Pendências',
            'Pendência até 30 dias',
            'Pendências 30 a 60',
            'Pendências Acima de 60',
            'Status',
        ];

        const linhas = registrosPagina.map((item) => [
            item.conciliador,
            item.revisor,
            item.areaResponsavel,
            `${formatarData(item.esteiraPeriodoInicio)} - ${formatarData(item.esteiraPeriodoFim)}`,
            item.contaContabil,
            item.descricaoConta,
            item.saldoBalancete.toFixed(2).replace('.', ','),
            item.saldoComSuporte.toFixed(2).replace('.', ','),
            item.totalPendencias.toFixed(2).replace('.', ','),
            item.pendenciaAte30Dias.toFixed(2).replace('.', ','),
            item.pendencia30a60Dias.toFixed(2).replace('.', ','),
            item.pendenciaAcima60Dias.toFixed(2).replace('.', ','),
            item.status,
        ]);

        const conteudo = [cabecalhos, ...linhas]
            .map((linha) =>
                linha
                    .map((coluna) => `"${String(coluna ?? '').replace(/"/g, '""')}"`)
                    .join(';')
            )
            .join('\n');

        const blob = new Blob([`﻿${conteudo}`], {
            type: 'text/csv;charset=utf-8;',
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${nomeArquivoBase}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const exportarPDF = () => {
        const titulo = `Relatório de Conciliação Contábil e Financeira - ${escapeHtml(usuarioAtivo)}`;
        const dataHora = new Date().toLocaleString('pt-BR');

        const linhasTabela = registrosPagina
            .map(
                (item) => `
                <tr>
                    <td>${escapeHtml(item.conciliador)}</td>
                    <td>${escapeHtml(item.revisor)}</td>
                    <td>${escapeHtml(item.areaResponsavel)}</td>
                    <td>${formatarData(item.esteiraPeriodoInicio)} - ${formatarData(item.esteiraPeriodoFim)}</td>
                    <td style="text-align:center; font-weight:700;">${escapeHtml(item.contaContabil)}</td>
                    <td>${escapeHtml(item.descricaoConta)}</td>
                    <td style="text-align:right;">${formatarMoeda(item.saldoBalancete)}</td>
                    <td style="text-align:right;">${formatarMoeda(item.saldoComSuporte)}</td>
                    <td style="text-align:right;">${item.totalPendencias > 0 ? formatarMoeda(item.totalPendencias) : '-'}</td>
                    <td style="text-align:right;">${item.pendenciaAte30Dias > 0 ? formatarMoeda(item.pendenciaAte30Dias) : '-'}</td>
                    <td style="text-align:right;">${item.pendencia30a60Dias > 0 ? formatarMoeda(item.pendencia30a60Dias) : '-'}</td>
                    <td style="text-align:right;">${item.pendenciaAcima60Dias > 0 ? formatarMoeda(item.pendenciaAcima60Dias) : '-'}</td>
                    <td style="text-align:center;">${escapeHtml(item.status)}</td>
                </tr>
            `
            )
            .join('');

        const html = `
            <html>
                <head>
                    <title>${titulo}</title>
                    <meta charset="utf-8" />
                    <style>
                        body {
                            font-family: Arial, Helvetica, sans-serif;
                            padding: 24px;
                            color: #222;
                        }
                        h1 {
                            font-size: 22px;
                            margin: 0 0 8px 0;
                            color: #2f5f4e;
                        }
                        .subinfo {
                            margin-bottom: 18px;
                            color: #555;
                            font-size: 13px;
                        }
                        .resumo {
                            display: grid;
                            grid-template-columns: repeat(4, 1fr);
                            gap: 12px;
                            margin-bottom: 20px;
                        }
                        .card {
                            border: 1px solid #d9dfdb;
                            border-radius: 10px;
                            padding: 12px;
                            text-align: center;
                        }
                        .card strong {
                            display: block;
                            margin-bottom: 6px;
                            font-size: 13px;
                            color: #2f5f4e;
                        }
                        .card span {
                            font-size: 16px;
                            font-weight: 700;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            font-size: 11px;
                        }
                        th {
                            background: #2f6b58;
                            color: #fff;
                            padding: 8px;
                            border: 1px solid #d9dfdb;
                            white-space: nowrap;
                        }
                        td {
                            padding: 8px;
                            border: 1px solid #d9dfdb;
                            vertical-align: top;
                        }
                        @media print {
                            body {
                                padding: 0;
                            }
                        }
                    </style>
                </head>
                <body>
                    <h1>${titulo}</h1>
                    <div class="subinfo">Gerado em: ${dataHora} — Página ${page} de ${totalPaginas} (${totalRegistros} contas no total; exportação limitada à página atual)</div>

                    <div class="resumo">
                        <div class="card">
                            <strong>Contas Pendentes</strong>
                            <span>${totaisRodape.contasPendentes}</span>
                        </div>
                        <div class="card">
                            <strong>Saldo Balancete</strong>
                            <span>${formatarMoeda(totaisRodape.saldoBalancete)}</span>
                        </div>
                        <div class="card">
                            <strong>Saldo com Suporte</strong>
                            <span>${formatarMoeda(totaisRodape.saldoComSuporte)}</span>
                        </div>
                        <div class="card">
                            <strong>Diferença Total</strong>
                            <span>-${formatarMoeda(totaisRodape.diferencaTotal)}</span>
                        </div>
                    </div>

                    <table>
                        <thead>
                            <tr>
                                <th>Conciliador</th>
                                <th>Revisão</th>
                                <th>Área Responsável</th>
                                <th>Período</th>
                                <th>Conta Contábil</th>
                                <th>Descrição Conta</th>
                                <th>Saldo Balancete</th>
                                <th>Saldo com Suporte</th>
                                <th>Total das Pendências</th>
                                <th>Pendência até 30 dias</th>
                                <th>Pendências 30 a 60</th>
                                <th>Pendências Acima de 60</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${linhasTabela || `
                                <tr>
                                    <td colspan="13" style="text-align:center; padding:20px;">
                                        Nenhum dado encontrado para exportação.
                                    </td>
                                </tr>
                            `}
                        </tbody>
                    </table>
                </body>
            </html>
        `;

        const janela = window.open('', '_blank', 'width=1400,height=900,noopener,noreferrer');
        if (!janela) return;

        janela.document.open();
        janela.document.write(html);
        janela.document.close();
        janela.focus();

        setTimeout(() => {
            janela.print();
        }, 400);
    };

    const cardStyle: CSSProperties = {
        background: cores.fundoCard,
        border: `1px solid ${cores.borda}`,
        borderRadius: '16px',
        boxShadow: cores.sombra,
        overflow: 'hidden',
        height: '100%',
    };

    const cardBodyStyle: CSSProperties = {
        padding: '16px',
    };

    const tituloCardStyle: CSSProperties = {
        fontSize: '18px',
        fontWeight: 700,
        color: cores.texto,
        marginBottom: '18px',
    };

    const subtituloStyle: CSSProperties = {
        fontSize: '14px',
        fontWeight: 700,
        color: cores.texto,
        marginBottom: '12px',
    };

    const headerTabelaStyle: CSSProperties = {
        background: `linear-gradient(180deg, ${cores.verdePrincipal} 0%, ${cores.verdeEscuro} 100%)`,
        color: '#ffffff',
        fontWeight: 800,
        fontSize: '12px',
        verticalAlign: 'middle',
        borderBottom: `1px solid ${cores.borda}`,
        padding: '10px 8px',
        whiteSpace: 'nowrap',
        textAlign: 'center',
    };

    const cellTabelaStyle: CSSProperties = {
        fontSize: '13px',
        color: cores.texto,
        verticalAlign: 'middle',
        borderBottom: `1px solid ${cores.bordaSuave}`,
        padding: '10px 8px',
        background: '#fbfcfb',
        whiteSpace: 'nowrap',
    };

    const botaoAcaoStyle: CSSProperties = {
        border: `1px solid ${cores.verdePrincipal}`,
        background: '#ffffff',
        color: cores.verdePrincipal,
        padding: '10px 14px',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: 800,
        cursor: 'pointer',
        minHeight: '40px',
    };

    const botaoPaginaStyle = (desabilitado: boolean): CSSProperties => ({
        border: `1px solid ${cores.borda}`,
        background: desabilitado ? cores.cinzaBotao : '#ffffff',
        color: desabilitado ? cores.textoSuave : cores.verdePrincipal,
        padding: '6px 12px',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: 700,
        cursor: desabilitado ? 'not-allowed' : 'pointer',
    });

    const badgeStatus = (status: StatusConciliacao) => {
        if (status === 'Conciliado') {
            return (
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '96px',
                        padding: '5px 12px',
                        borderRadius: '999px',
                        background: cores.verdeClaro,
                        color: '#ffffff',
                        fontSize: '12px',
                        fontWeight: 800,
                        lineHeight: 1,
                    }}
                >
                    Conciliado
                </span>
            );
        }

        if (status === 'Conciliado c/ Pendência') {
            return (
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '150px',
                        padding: '5px 12px',
                        borderRadius: '999px',
                        background: cores.amarelo,
                        color: '#6b4e00',
                        fontSize: '12px',
                        fontWeight: 800,
                        lineHeight: 1,
                    }}
                >
                    Conciliado c/ Pendência
                </span>
            );
        }

        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '88px',
                    padding: '5px 12px',
                    borderRadius: '999px',
                    background: '#fdd8d4',
                    color: '#b3261e',
                    fontSize: '12px',
                    fontWeight: 800,
                    lineHeight: 1,
                }}
            >
                Pendente
            </span>
        );
    };

    const BarraResumo = ({
        label,
        pendente,
        conciliadoPendencia,
        conciliado,
    }: {
        label: string;
        pendente: number;
        conciliadoPendencia: number;
        conciliado: number;
    }) => (
        <div style={{ marginBottom: '10px' }}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '88px 1fr 40px',
                    gap: '10px',
                    alignItems: 'center',
                }}
            >
                <div
                    style={{
                        fontSize: '13px',
                        color: cores.texto,
                        fontWeight: 600,
                    }}
                >
                    {label}
                </div>

                <div
                    style={{
                        height: '14px',
                        width: '100%',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        display: 'flex',
                        background: '#ecefed',
                        border: `1px solid ${cores.bordaSuave}`,
                    }}
                >
                    <div style={{ width: `${conciliado}%`, background: cores.verdeClaro }} />
                    <div style={{ width: `${conciliadoPendencia}%`, background: cores.amarelo }} />
                    <div style={{ width: `${pendente}%`, background: cores.laranja }} />
                </div>

                <div
                    style={{
                        fontSize: '13px',
                        color: cores.texto,
                        fontWeight: 800,
                    }}
                >
                    {Math.round(conciliado + conciliadoPendencia + pendente)}%
                </div>
            </div>
        </div>
    );

    const TabUsuario = (nome: string) => {
        const ativo = usuarioAtivo === nome;

        return (
            <button
                key={nome}
                type='button'
                onClick={() => selecionarConciliador(nome)}
                style={{
                    border: `1px solid ${ativo ? cores.verdePrincipal : cores.borda}`,
                    background: ativo ? cores.verdePrincipal : cores.cinzaBotao,
                    color: ativo ? '#ffffff' : cores.textoSuave,
                    padding: '10px 16px',
                    borderRadius: '0px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    minHeight: '40px',
                }}
            >
                {nome}
            </button>
        );
    };

    if (carregando && registrosPagina.length === 0) {
        return (
            <div className='row g-5'>
                <div className='col-12'>
                    <div style={cardStyle}>
                        <div style={cardBodyStyle}>Carregando dashboard...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (erro) {
        return (
            <div className='row g-5'>
                <div className='col-12'>
                    <div style={cardStyle}>
                        <div style={{ ...cardBodyStyle, color: cores.vermelho }}>{erro}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='row g-5'>
            <div className='col-sm-12 col-xl-4'>
                <div style={cardStyle}>
                    <div style={cardBodyStyle}>
                        <div style={tituloCardStyle}>
                            Visão Geral - Status de Conciliação {usuarioAtivo !== 'Geral' ? `(${usuarioAtivo})` : '(Todos os funcionários)'}
                        </div>

                        <div style={subtituloStyle}>Status das Contas</div>

                        <div className='row align-items-center mb-8'>
                            <div className='col-md-6' style={{ height: 220 }}>
                                <ResponsiveContainer width='100%' height='100%'>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx='50%'
                                            cy='50%'
                                            innerRadius={58}
                                            outerRadius={95}
                                            dataKey='value'
                                            paddingAngle={0}
                                            labelLine={false}
                                            label={({ value }) => `${value}%`}
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value: number) => `${value}%`} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            <div className='col-md-6'>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '10px',
                                        fontSize: '14px',
                                        color: '#4b4f52',
                                    }}
                                >
                                    <span
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 2,
                                            backgroundColor: cores.vermelho,
                                            display: 'inline-block',
                                            marginRight: 10,
                                        }}
                                    />
                                    <span>Pendentes</span>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '10px',
                                        fontSize: '14px',
                                        color: '#4b4f52',
                                    }}
                                >
                                    <span
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 2,
                                            backgroundColor: cores.amarelo,
                                            display: 'inline-block',
                                            marginRight: 10,
                                        }}
                                    />
                                    <span>Conciliado c/ Pendência</span>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '10px',
                                        fontSize: '14px',
                                        color: '#4b4f52',
                                    }}
                                >
                                    <span
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 2,
                                            backgroundColor: cores.verdeClaro,
                                            display: 'inline-block',
                                            marginRight: 10,
                                        }}
                                    />
                                    <span>Conciliado</span>
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                borderTop: '1px solid #ececec',
                                paddingTop: '12px',
                            }}
                        >
                            <div style={subtituloStyle}>Total de Contas Pendentes no Mês</div>

                            <div style={{ marginBottom: '12px' }}>
                                {totaisGrupos.map((item) => (
                                    <BarraResumo
                                        key={item.grupo}
                                        label={item.grupo}
                                        pendente={item.pendentesPercentual}
                                        conciliadoPendencia={item.conciliadoPendenciaPercentual}
                                        conciliado={item.conciliadoPercentual}
                                    />
                                ))}
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '14px',
                                    flexWrap: 'wrap',
                                    marginTop: '12px',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ width: 12, height: 12, background: cores.vermelho, borderRadius: 2 }} />
                                    <span style={{ fontSize: '12px', color: cores.textoSuave, fontWeight: 700 }}>Pendentes</span>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ width: 12, height: 12, background: cores.amarelo, borderRadius: 2 }} />
                                    <span style={{ fontSize: '12px', color: cores.textoSuave, fontWeight: 700 }}>Conciliado c/ Pendência</span>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ width: 12, height: 12, background: cores.verdeClaro, borderRadius: 2 }} />
                                    <span style={{ fontSize: '12px', color: cores.textoSuave, fontWeight: 700 }}>Conciliado</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='col-sm-12 col-xl-8'>
                <div style={cardStyle}>
                    <div style={{ ...cardBodyStyle, paddingBottom: '12px' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '12px',
                                flexWrap: 'wrap',
                                marginBottom: '14px',
                            }}
                        >
                            <h3
                                style={{
                                    fontSize: '20px',
                                    fontWeight: 800,
                                    color: cores.titulo,
                                    marginBottom: 0,
                                }}
                            >
                                Relatório de Conciliação Contábil e Financeira
                            </h3>

                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button type='button' onClick={exportarPDF} style={botaoAcaoStyle}>
                                    Exportar PDF
                                </button>
                                <button type='button' onClick={exportarExcel} style={botaoAcaoStyle}>
                                    Exportar Excel
                                </button>
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                border: `1px solid ${cores.borda}`,
                                borderBottom: 'none',
                                borderRadius: '8px 8px 0 0',
                                overflow: 'hidden',
                            }}
                        >
                            {usuarios.map((usuario) => TabUsuario(usuario))}
                        </div>

                        <div className='table-responsive'>
                            <table className='table align-middle mb-0'>
                                <thead>
                                    <tr>
                                        <th style={headerTabelaStyle}>Conciliador</th>
                                        <th style={headerTabelaStyle}>Revisão</th>
                                        <th style={headerTabelaStyle}>Área Responsável</th>
                                        <th style={headerTabelaStyle}>Período</th>
                                        <th style={headerTabelaStyle}>Conta Contábil</th>
                                        <th style={headerTabelaStyle}>Descrição Conta</th>
                                        <th style={headerTabelaStyle}>Saldo Balancete</th>
                                        <th style={headerTabelaStyle}>Saldo com Suporte</th>
                                        <th style={headerTabelaStyle}>Total das Pendências</th>
                                        <th style={headerTabelaStyle}>Pendência até 30 dias</th>
                                        <th style={headerTabelaStyle}>Pendências 30 a 60</th>
                                        <th style={headerTabelaStyle}>Pendências Acima de 60</th>
                                        <th style={headerTabelaStyle}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {registrosPagina.map((item) => (
                                        <tr key={`${item.relatorioResumidoID}-${item.esteiraPeriodoInicio}`}>
                                            <td style={cellTabelaStyle}>{item.conciliador}</td>
                                            <td style={cellTabelaStyle}>{item.revisor}</td>
                                            <td style={cellTabelaStyle}>{item.areaResponsavel}</td>
                                            <td style={cellTabelaStyle}>
                                                {formatarData(item.esteiraPeriodoInicio)} - {formatarData(item.esteiraPeriodoFim)}
                                            </td>
                                            <td style={{ ...cellTabelaStyle, textAlign: 'center', fontWeight: 700 }}>{item.contaContabil}</td>
                                            <td style={cellTabelaStyle}>{item.descricaoConta}</td>
                                            <td style={{ ...cellTabelaStyle, fontWeight: 700, textAlign: 'right' }}>
                                                {formatarMoeda(item.saldoBalancete)}
                                            </td>
                                            <td style={{ ...cellTabelaStyle, fontWeight: 700, textAlign: 'right' }}>
                                                {formatarMoeda(item.saldoComSuporte)}
                                            </td>
                                            <td style={{ ...cellTabelaStyle, fontWeight: 800, textAlign: 'right' }}>
                                                {item.totalPendencias > 0 ? formatarMoeda(item.totalPendencias) : '-'}
                                            </td>
                                            <td style={{ ...cellTabelaStyle, fontWeight: 700, textAlign: 'right' }}>
                                                {item.pendenciaAte30Dias > 0 ? formatarMoeda(item.pendenciaAte30Dias) : '-'}
                                            </td>
                                            <td style={{ ...cellTabelaStyle, fontWeight: 700, textAlign: 'right' }}>
                                                {item.pendencia30a60Dias > 0 ? formatarMoeda(item.pendencia30a60Dias) : '-'}
                                            </td>
                                            <td style={{ ...cellTabelaStyle, fontWeight: 700, textAlign: 'right' }}>
                                                {item.pendenciaAcima60Dias > 0 ? formatarMoeda(item.pendenciaAcima60Dias) : '-'}
                                            </td>
                                            <td style={{ ...cellTabelaStyle, textAlign: 'center' }}>{badgeStatus(item.status)}</td>
                                        </tr>
                                    ))}

                                    {registrosPagina.length === 0 && (
                                        <tr>
                                            <td colSpan={13} style={{ ...cellTabelaStyle, textAlign: 'center', padding: '18px' }}>
                                                Nenhuma conta encontrada para este filtro.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px',
                                padding: '12px 4px 4px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: cores.textoSuave }}>
                                <span>Contas por página:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => selecionarPerPage(parseInt(e.target.value))}
                                    className='form-select form-select-sm'
                                    style={{ width: 'auto' }}
                                >
                                    {OPCOES_PER_PAGE.map((opcao) => (
                                        <option key={opcao} value={opcao}>
                                            {opcao}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ fontSize: '13px', color: cores.textoSuave, fontWeight: 600 }}>
                                Página {page} de {totalPaginas} — {totalRegistros} conta{totalRegistros === 1 ? '' : 's'} no total
                            </div>

                            <div style={{ display: 'flex', gap: '6px' }}>
                                <button
                                    type='button'
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    style={botaoPaginaStyle(page <= 1)}
                                >
                                    Anterior
                                </button>
                                <button
                                    type='button'
                                    disabled={page >= totalPaginas}
                                    onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
                                    style={botaoPaginaStyle(page >= totalPaginas)}
                                >
                                    Próxima
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='col-12'>
                <div style={cardStyle}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1.2fr 1.4fr 1.4fr 1.5fr',
                        }}
                    >
                        <div
                            style={{
                                padding: '16px 18px',
                                borderRight: `1px solid ${cores.borda}`,
                                textAlign: 'center',
                            }}
                        >
                            <div style={{ fontSize: '16px', fontWeight: 700, color: cores.texto, marginBottom: '10px' }}>
                                Contas Pendentes
                            </div>

                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: '24px',
                                        fontWeight: 900,
                                        color: cores.vermelho,
                                        borderBottom: `3px solid ${cores.vermelho}`,
                                        lineHeight: 1,
                                        paddingBottom: '2px',
                                    }}
                                >
                                    {totaisRodape.contasPendentes}
                                </span>
                            </div>
                        </div>

                        <div
                            style={{
                                padding: '16px 18px',
                                borderRight: `1px solid ${cores.borda}`,
                                textAlign: 'center',
                            }}
                        >
                            <div style={{ fontSize: '16px', fontWeight: 700, color: cores.titulo, marginBottom: '10px' }}>
                                Saldo Balancete
                            </div>
                            <div style={{ fontSize: '22px', fontWeight: 900, color: cores.titulo }}>
                                {formatarMoeda(totaisRodape.saldoBalancete)}
                            </div>
                        </div>

                        <div
                            style={{
                                padding: '16px 18px',
                                borderRight: `1px solid ${cores.borda}`,
                                textAlign: 'center',
                            }}
                        >
                            <div style={{ fontSize: '16px', fontWeight: 700, color: cores.titulo, marginBottom: '10px' }}>
                                Saldo com Suporte
                            </div>
                            <div style={{ fontSize: '22px', fontWeight: 900, color: cores.titulo }}>
                                {formatarMoeda(totaisRodape.saldoComSuporte)}
                            </div>
                        </div>

                        <div
                            style={{
                                padding: '16px 18px',
                                textAlign: 'center',
                            }}
                        >
                            <div style={{ fontSize: '16px', fontWeight: 700, color: cores.texto, marginBottom: '10px' }}>
                                Diferença Total
                            </div>
                            <div style={{ fontSize: '22px', fontWeight: 900, color: cores.vermelho }}>
                                -{formatarMoeda(totaisRodape.diferencaTotal)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardConciliacaoContabilFinanceira;
